import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { careUnitsQueryOptions } from "../../../features/care-units/api/care-units";
import { CareUnitAdmin } from "../../../features/care-units/components/care-unit-admin";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { FacilityChooser } from "../../../features/facility-pick/facility-chooser";

/**
 * `RPM-031`'s care-unit administration list (`J-224`), with each unit's History (`RPM-063`, `J-291`).
 *
 * THE ROUTE COMPOSES FEATURES, as `.claude/rules/coding.md` asks: the chooser is `facility-pick`'s,
 * the list is `care-units`'s and the History tab is `change-history`'s, and the building is the fact
 * they share. The tab is rendered only for an account holding `audit: ["read"]`; a clinician, who may
 * read the units, sees the list with no tab. The server decides again on every request.
 */
function CareUnitsScreen() {
  const [facilityId, setFacilityId] = useState("");
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadHistory = usePermission({ audit: ["read"] });
  const canChange = usePermission({ careUnit: ["update"] }, ["TENANT", "CERTIFIED_FACILITY"]);
  const units = useQuery({
    ...careUnitsQueryOptions(facilityId),
    enabled: canReadHistory && facilityId !== "",
  });
  return (
    <div className="flex flex-col gap-4 pt-4">
      <FacilityChooser value={facilityId} onChange={setFacilityId} />
      {facilityId === "" ? null : (
        <HistoryTabs
          showHistory={canReadHistory}
          tab={tab}
          onTabChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
          }
          detailsLabel="Care units"
          details={<CareUnitAdmin key={facilityId} facilityId={facilityId} readOnly={!canChange} />}
          history={
            <RecordHistory
              key={facilityId}
              entity="care-units"
              records={(units.data?.items ?? []).map((u) => ({ id: u.careUnitId, label: u.label }))}
              noun="Care unit"
              caption={(label) => `Change history of care unit ${label}`}
              columnLabels={{ is_active: "Active", mirror_unit_values: "Mirror unit values" }}
            />
          }
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/care-units")({
  validateSearch: historyTabSearchSchema,
  component: CareUnitsScreen,
  staticData: { screenName: "Care units" },
});
